import { CognitoUser } from "@aws-amplify/auth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Auth } from "aws-amplify";
import * as localStorageKeys from "data/local-storage-keys";
import { ReactQueryMutationError } from "data/mutations-common";
import { CheckCircle } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { ChangeEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";

interface MfaSetupProps {
  onSubmit: () => void;
  onOptOut: () => void;
  cognitoUser: CognitoUser;
  style?: React.CSSProperties;
  startFromStep?: number;
  createUserOrg?: () => void;
}

export const MfaSetup = (props: MfaSetupProps) => {
  const [user] = useState<CognitoUser | null>(props.cognitoUser);
  const [qrCode, setQrCode] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [step, setStep] = useState(props.startFromStep ?? 0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsVerifyCode, setSmsVerifyCode] = useState("");

  const setupTotpMutation = useMutation({
    mutationFn: () => Auth.setupTOTP(user),
  });
  const verifyTotpTokenMutation = useMutation({
    mutationFn: () => Auth.verifyTotpToken(user, mfaCode),
    onSuccess: async () => {
      await setPreferredMFAMutation.mutateAsync();
      setQrCode("");
      setStep(3);
    },
  });
  const setPreferredMFAMutation = useMutation({
    mutationFn: () => Auth.setPreferredMFA(user, "TOTP"),
  });
  const setupSMSMutation = useMutation({
    mutationFn: async () => {
      await Auth.updateUserAttributes(user, { phone_number: phoneNumber });
      Auth.verifyCurrentUserAttribute("phone_number");
    },
  });
  const verifySMSMutation = useMutation({
    mutationFn: () =>
      Auth.verifyCurrentUserAttributeSubmit("phone_number", smsVerifyCode).then(
        () => Auth.setPreferredMFA(user, "SMS_MFA"),
      ),
    onSuccess: () => setStep(3),
  });

  const onPhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const regex = /^\+(\d{0,11})$/;
    if (regex.test(value) || value === "") setPhoneNumber(value);
  };

  const onCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMfaCode(event.target.value.replace(/[^0-9]/g, ""));
  };

  const onSmsCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSmsVerifyCode(event.target.value.replace(/[^0-9]/g, ""));
  };

  useEffect(() => {
    const setupTOTP = async () => {
      const code = await setupTotpMutation.mutateAsync();
      setQrCode(
        `otpauth://totp/Geoscape Hub:${user?.getUsername()}?secret=${code}&issuer=Geoscape Hub`,
      );
    };
    setupTOTP();
  }, []);

  const verifyTOTP = async () => {
    verifyTotpTokenMutation.mutateAsync();
  };

  return (
    <>
      {step == 0 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>Setup Multi Factor Authentication</CardTitle>
            <CardDescription>
              Geoscape recommends you protect your account with MFA (Multi
              Factor Authentication), this is optional but recommended for
              security.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button className="w-full" onClick={() => setStep(1)}>
              Setup MFA
            </Button>
            <Button
              id="optOutMfa"
              variant="outline"
              className="w-full"
              onClick={() => setStep(-1)}
            >
              OPT OUT
            </Button>
          </CardFooter>
        </Card>
      )}

      {step == 1 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>MFA Method</CardTitle>
            <CardDescription>
              Geoscape supports two methods of MFA. You'll only be able to use
              one method at a time. We highly recommend TOTP.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button className="w-full" onClick={() => setStep(2)}>
              Authenticator App (TOTP)
            </Button>
            <Button className="w-full" onClick={() => setStep(-2)}>
              Text Messaging (SMS)
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setStep(0)}
            >
              Back
            </Button>
          </CardFooter>
        </Card>
      )}

      {step == -1 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>Opt out of MFA?</CardTitle>
            <CardDescription>
              Are you sure you want to opt out of MFA? Geoscape recommends you
              protect your account with MFA. You will be able to configure MFA
              at a later date from inside your account settings.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button
              id="confirmOptOutMfa"
              className="w-full"
              onClick={() => {
                localStorage.setItem(localStorageKeys.optOutMfa, "true");
                props.onOptOut();
              }}
            >
              Confirm Opt Out
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setStep(0)}
            >
              Back
            </Button>
          </CardFooter>
        </Card>
      )}

      {step == 2 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>Setup Multi Factor Authentication</CardTitle>
            <CardDescription>
              Use your authenticator app to scan the QR code displayed below
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {setupTotpMutation.isPending && (
              <Skeleton className="size-[225px] self-center" />
            )}
            {setupTotpMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  An error occured when trying to generate QR code
                </AlertDescription>
              </Alert>
            )}
            {setupTotpMutation.isSuccess && !!qrCode && (
              <>
                <div className="self-center size-[225px]">
                  <QRCodeSVG value={qrCode} size={225} />
                </div>
                {verifyTotpTokenMutation.isError && (
                  <Alert variant="destructive">
                    <AlertDescription>
                      {
                        (
                          verifyTotpTokenMutation.error as ReactQueryMutationError
                        ).message
                      }
                    </AlertDescription>
                  </Alert>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="totpCode">TOTP Code</Label>
                  <Input
                    id="totpCode"
                    maxLength={6}
                    disabled={
                      setupTotpMutation.isPending ||
                      verifyTotpTokenMutation.isPending ||
                      setPreferredMFAMutation.isPending
                    }
                    value={mfaCode}
                    onChange={onCodeChange}
                    onKeyDown={async (ev) => {
                      if (ev.key === "Enter" && mfaCode.length === 6)
                        await verifyTOTP();
                    }}
                  />
                </div>
              </>
            )}
          </CardContent>
          <CardFooter>
            {setupTotpMutation.isSuccess && (
              <Button
                className="w-full"
                disabled={setupTotpMutation.isPending || mfaCode.length !== 6}
                onClick={async () => await verifyTOTP()}
              >
                {verifyTotpTokenMutation.isPending ||
                setPreferredMFAMutation.isPending ? (
                  <Spinner />
                ) : (
                  "Submit"
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      )}

      {step == -2 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>Setup SMS MFA</CardTitle>
            <CardDescription>
              We'll text a verification code to this mobile device whenever you
              sign in to Geoscape Hub. Standard SMS charges may apply from your
              operator.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                id="phoneNumber"
                required
                maxLength={12}
                placeholder="+61xxxxxxxxx"
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                onKeyDown={async (ev) => {
                  if (ev.key === "Enter" && phoneNumber.length === 12)
                    await setupSMSMutation.mutateAsync();
                }}
              />
              <p className="text-xs text-muted-foreground">
                By providing your mobile number, you agree to receive text
                messages from Geoscape.
              </p>
            </div>
            <Button
              className="w-full"
              disabled={
                setupSMSMutation.isPending ||
                !phoneNumber ||
                phoneNumber.length !== 12
              }
              onClick={async () => await setupSMSMutation.mutateAsync()}
            >
              {setupSMSMutation.isPending ? <Spinner /> : "Send Code"}
            </Button>
            <div className="space-y-1.5">
              <Label htmlFor="smsVerifyCode">Verification Code</Label>
              <Input
                id="smsVerifyCode"
                maxLength={6}
                value={smsVerifyCode}
                onChange={onSmsCodeChange}
                onKeyDown={async (ev) => {
                  if (ev.key === "Enter" && smsVerifyCode.length === 6)
                    await verifySMSMutation.mutateAsync();
                }}
              />
            </div>
            {verifySMSMutation.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {(verifySMSMutation.error as ReactQueryMutationError).message}
                </AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full"
              disabled={
                verifySMSMutation.isPending ||
                !smsVerifyCode ||
                smsVerifyCode.length !== 6
              }
              onClick={async () => await verifySMSMutation.mutateAsync()}
            >
              {verifySMSMutation.isPending ? <Spinner /> : "Verify"}
            </Button>
            {props.startFromStep == 0 && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setStep(1)}
              >
                Back
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card style={props.style ?? {}}>
          <CardHeader>
            <CardTitle>Success!</CardTitle>
            <CardDescription>
              MFA has successfully been setup for your account.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <CheckCircle className="size-10 text-success" />
          </CardContent>
          <CardFooter>
            <Button className="w-full" onClick={() => props.onSubmit()}>
              Done
            </Button>
          </CardFooter>
        </Card>
      )}
    </>
  );
};
